/**
 * Authentication Service
 * Module 07 - Dashboard UI
 * 
 * Handles user authentication operations
 */

import api from './api';
import { AuthResponse, LoginCredentials, RegisterCredentials, User } from '../types';

/**
 * Transform API user response (snake_case) to frontend format (camelCase)
 */
function transformUserFromApi(apiUser: any): User {
  return {
    id: apiUser.id,
    email: apiUser.email,
    role: apiUser.role,
    storageQuota: apiUser.storage_quota,
    storageUsed: apiUser.storage_used,
    embedQuota: apiUser.embed_quota,
    embedsUsed: apiUser.embeds_used,
    isVerified: apiUser.is_verified,
    createdAt: apiUser.created_at,
    updatedAt: apiUser.updated_at || apiUser.created_at, // Fallback if updated_at not present
  };
}

export const authService = {
  /**
   * Login user with email and password
   * 
   * @param credentials - Login credentials
   * @returns Auth response with token and user data
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<any>('/auth/login', credentials);
    return {
      token: response.data.token,
      user: transformUserFromApi(response.data.user),
    };
  },

  /**
   * Register new user account
   * 
   * @param credentials - Registration credentials
   * @returns Auth response with token and user data
   */
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const { confirmPassword, ...registerData } = credentials;
    await api.post<any>('/auth/register', registerData);
    
    // After registration, we need to login to get the token and full user data
    return await this.login({
      email: credentials.email,
      password: credentials.password,
    });
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Logout endpoint might fail if token is already invalid, ignore errors
      console.warn('Logout request failed:', error);
    }
  },

  /**
   * Validate current authentication token
   * 
   * @returns True if token is valid
   */
  async validateToken(): Promise<boolean> {
    try {
      await api.get('/auth/validate');
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get current user profile
   * 
   * @returns User profile data
   */
  async getProfile(): Promise<User> {
    const response = await api.get<any>('/auth/profile');
    return transformUserFromApi(response.data);
  },

  /**
   * Request password reset
   * 
   * @param email - User email address
   */
  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/auth/reset-password', { email });
  },

  /**
   * Confirm password reset with token
   * 
   * @param token - Reset token from email
   * @param newPassword - New password
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password/confirm', { token, newPassword });
  },

  /**
   * Verify email address using the token from the emailed link
   *
   * @param token - Verification token from email
   */
  async verifyEmail(token: string): Promise<void> {
    await api.post('/auth/verify-email', { token });
  },

  /**
   * Resend the verification email for the current authenticated user
   */
  async resendVerification(): Promise<void> {
    await api.post('/auth/resend-verification');
  },
};
